/**
 * PDF Service for LT Cases Druckplan
 * Handles data extraction from build results and PDF generation using pdfme
 */

import type { Schema, Template } from "@pdfme/common";
import { type AbstractPartDefinition, Sides } from "@project/emerge-types";
import {
  calculateRivetSpacing,
  isArticleFreePart,
  isPartHiddenFromPlan,
  type PartLike,
} from "@project/pricing";
import { InteriorType } from "../enum/InteriorType";
import { NodeType } from "../enum/NodeType";
import { PanelType } from "../enum/PanelType";
import { LTPartType } from "../enum/PartType";
import type { CaseInteriorOptions } from "../interfaces/AccessoryCompartmentOptions";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { PadDefinition } from "../interfaces/PadProperties";
import { DefaultGroupName } from "../structure/Rect";
import { SidesNames } from "../translations/Sides";
import {
  getAngleSectionCutSign,
  normalizeCuts,
  normalizeCutsKey,
} from "../utils/angleSectionCuts";
import { formatPriceDisplay } from "./formatPrice";
import type { InteriorLayoutData } from "./interiorSchema";
import { hexToRgb } from "./interiorSchema";
import {
  Align,
  type CellRenderContext,
  registerCellRenderer,
  type TableColumn,
  type TableData,
} from "./tableSchema";
import {
  type DynamicHeights,
  emptyDruckplanInputs,
  getDynamicTemplate,
  PDF_TEXT_SIZE,
  PDF_TITLE_SIZE,
} from "./template";
import type {
  FramedTextValue,
  PdfAngleSectionEntry,
  PdfAngleSectionGroup,
  PdfCaseHeaderData,
  PdfInputData,
  PdfPaddingEntry,
  PdfPanelDividerSortMeta,
  PdfPanelEntry,
  PdfPanelMainSortMeta,
  PdfPositionInput,
  PdfProfileEntry,
  PdfRackRailEntry,
  PdfSupplyPartEntry,
} from "./types";

// =============================================================================
// TYPES
// =============================================================================

/** 3D point as object with x, y, z coordinates */
type Point3D = { x: number; y: number; z: number };

/** 3D point in either object or tuple format */
type Point3DOrTuple = Point3D | [number, number, number];

// =============================================================================
// FORMATTING HELPERS
// =============================================================================

/**
 * Format dimensions as string "W × H × D"
 */
function formatDimensions(x: number, y: number, z: number): string {
  return `${Math.round(x)} × ${Math.round(y)} × ${Math.round(z)}`;
}

/** German calendar date in PDF (always two-digit day/month, e.g. 06.05.2026). */
const PDF_DATE_LOCALE_OPTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};

/**
 * Format date for display
 */
function formatDate(date?: string | Date): string {
  if (!date)
    return new Date().toLocaleDateString("de-DE", PDF_DATE_LOCALE_OPTS);
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE", PDF_DATE_LOCALE_OPTS);
}

function getColorName(color: unknown): string {
  if (!color) return "";
  if (typeof color === "string") return color;
  if (typeof color === "object" && color !== null) {
    const c = color as { description?: string; name?: string; color?: string };
    return c.description ?? c.name ?? c.color ?? "";
  }
  if (typeof color === "number" || typeof color === "boolean") {
    return String(color);
  }
  return "";
}

// =============================================================================
// DATA COLLECTORS
// =============================================================================

/** SB field: prefer `userFirstName`, else first whitespace-separated word of `userDisplayName`. */
function sachbearbeiterFirstNameForPdf(input: PdfPositionInput): string {
  const fromProfile = input.userFirstName?.trim();
  if (fromProfile) return fromProfile;
  const display = (input.userDisplayName ?? "").trim();
  if (!display) return "";
  const [first] = display.split(/\s+/);
  return first ?? "";
}

/**
 * Collect header data from settings, blueprint and position input
 */
function collectHeaderData(
  settings: FlightCaseSettings,
  blueprint: FlightCaseBlueprint,
  positionInput: PdfPositionInput,
): PdfCaseHeaderData {
  const inner = blueprint.innerDimensions;
  const outer = blueprint.outerDimensions;

  return {
    // AFS#: comes from UI field "AFS#"
    afsNumber: positionInput.afsNumber ?? "",
    // Artikel#: comes from UI field "Artikel#"
    articleNumber: positionInput.articleNumber ?? "",
    caseType: settings.type ?? "",
    customer: positionInput.customer ?? "",
    date: formatDate(positionInput.date),
    model: positionInput.model ?? "",
    innerDimensions: formatDimensions(inner.x, inner.y, inner.z),
    outerDimensions: formatDimensions(outer.x, outer.y, outer.z),
    panelStrength: `${settings.exteriorOptions?.strength ?? ""}`,
    angleSectionType:
      settings.exteriorOptions?.angleSections?.description ?? "",
    price: positionInput.price
      ? `${formatPriceDisplay(positionInput.price)} €`
      : "",
    userDisplayName: sachbearbeiterFirstNameForPdf(positionInput),
    variant: positionInput.variant ?? "",
    // Pattern (Source): comes from UI field "Muster"
    pattern: positionInput.pattern ?? "",
  };
}

/**
 * Check if a panel should be filtered out based on side filter
 * Sides: TOP=5, BOTTOM=4, FRONT=0, BACK=1, LEFT=2, RIGHT=3
 */
function shouldFilterPanel(
  sideFilter: "underlay" | "divider" | undefined,
  interiorType: string | undefined,
  side: number | undefined,
): boolean {
  if (!sideFilter) return false;

  if (sideFilter === "underlay") {
    // Underlays are only BOTTOM side panels that are NOT rack dividers
    // Rack interiors don't have underlays - all their panels are dividers
    if (interiorType === "RACK") return true;
    if (side !== 4) return true;
  }

  if (sideFilter === "divider") {
    // Dividers: For case = non-BOTTOM, For rack = all TrayPanels
    if (interiorType !== "RACK" && side === 4) return true;
  }

  return false;
}

function mergePdfSortMain(
  existing: PdfPanelMainSortMeta | undefined,
  incoming: PdfPanelMainSortMeta,
): PdfPanelMainSortMeta {
  if (!existing) return incoming;
  return {
    overlaps: Math.max(existing.overlaps, incoming.overlaps),
    isCorpus: Math.max(existing.isCorpus, incoming.isCorpus) as 0 | 1,
  };
}

function mergePdfSortDivider(
  existing: PdfPanelDividerSortMeta | undefined,
  incoming: PdfPanelDividerSortMeta,
): PdfPanelDividerSortMeta {
  if (!existing) return incoming;
  return {
    notePenalty: Math.min(existing.notePenalty, incoming.notePenalty),
    strength: Math.min(existing.strength, incoming.strength),
    sideEnum: Math.max(existing.sideEnum, incoming.sideEnum),
  };
}

/** Legacy PdfPlan (old react app): OuterPanel / RollerBoard / SlideRail / Underlay rows */
function getMainPanelPdfSort(
  part: AbstractPartDefinition<string>,
): PdfPanelMainSortMeta {
  const p = part as unknown as {
    panelType?: PanelType;
    overlaps?: number;
    group?: string;
  };
  if (p.panelType === PanelType.OuterPanel) {
    return {
      overlaps: p.overlaps ?? 0,
      isCorpus: p.group === DefaultGroupName ? 1 : 0,
    };
  }
  if (
    p.panelType === PanelType.RollerBoard ||
    p.panelType === PanelType.SlideRail ||
    p.panelType === PanelType.Underlay
  ) {
    return { overlaps: -1, isCorpus: 0 };
  }
  if (typeof p.overlaps === "number") {
    return {
      overlaps: p.overlaps,
      isCorpus: p.group === DefaultGroupName ? 1 : 0,
    };
  }
  return { overlaps: -1, isCorpus: 0 };
}

/** Legacy sortPanels: empty note first, strength asc, side enum desc */
function getDividerPdfSortFromTray(
  part: AbstractPartDefinition<string>,
): PdfPanelDividerSortMeta {
  const props = part as unknown as {
    note?: string;
    strength?: number;
    side?: number;
  };
  const note = props.note ?? "";
  return {
    notePenalty: note.length === 0 ? 0 : 1,
    strength: props.strength ?? 0,
    sideEnum: props.side ?? -1,
  };
}

function getDividerPdfSortFromRackUnderlay(
  part: AbstractPartDefinition<string>,
): PdfPanelDividerSortMeta {
  const props = part as unknown as {
    note?: string;
    strength?: number;
    position?: Point3D;
  };
  const note = props.note ?? "";
  const x = props.position?.x ?? 0;
  const sideEnum = x < 0 ? Sides.LEFT : Sides.RIGHT;
  return {
    notePenalty: note.length === 0 ? 0 : 1,
    strength: props.strength ?? 0,
    sideEnum,
  };
}

function comparePdfMainPanelsLegacy(
  a: PdfPanelEntry,
  b: PdfPanelEntry,
): number {
  const ma = a.pdfSortMain;
  const mb = b.pdfSortMain;
  const oa = ma?.overlaps ?? -1;
  const ob = mb?.overlaps ?? -1;
  if (ob !== oa) return ob - oa;
  const ca = ma?.isCorpus ?? 0;
  const cb = mb?.isCorpus ?? 0;
  // Matches lodash orderBy(..., ["desc","desc"]) on overlaps then isCorpus (comment there was wrong)
  if (cb !== ca) return cb - ca;
  // Legacy did not sort equal-key panels by area; lodash kept input order.
  const sa = a.pdfSortStable ?? Number.MAX_SAFE_INTEGER;
  const sb = b.pdfSortStable ?? Number.MAX_SAFE_INTEGER;
  return sa - sb;
}

function comparePdfDividerLegacy(a: PdfPanelEntry, b: PdfPanelEntry): number {
  const da = a.pdfSortDivider;
  const db = b.pdfSortDivider;
  const ap = da?.notePenalty ?? (a.note?.length ? 1 : 0);
  const bp = db?.notePenalty ?? (b.note?.length ? 1 : 0);
  if (ap !== bp) return ap - bp;
  const as = da?.strength ?? (Number.parseFloat(a.strength || "0") || 0);
  const bs = db?.strength ?? (Number.parseFloat(b.strength || "0") || 0);
  if (as !== bs) return as - bs;
  const aside = da?.sideEnum ?? -1;
  const bside = db?.sideEnum ?? -1;
  return bside - aside;
}

/**
 * Generic helper to increment count for existing entry or return false if new entry needed
 */
function incrementEntryCount<T extends { countNum: number; count: string }>(
  map: Map<string, T>,
  key: string,
): boolean {
  const existing = map.get(key);
  if (existing) {
    existing.countNum += 1;
    existing.count = existing.countNum.toString();
    return true;
  }
  return false;
}

/**
 * Generic helper to remove countNum from map values and return array
 */
function removeCountNumFromMapValues<T extends { countNum: number }>(
  map: Map<string, T>,
): Omit<T, "countNum">[] {
  return Array.from(map.values()).map(({ countNum, ...rest }) => rest);
}

interface AddPanelOptions {
  panels: Map<string, PdfPanelEntry & { countNum: number; sides?: string[] }>;
  key: string;
  props: {
    group?: string;
    note?: string;
  };
  colorName: string;
  width: number;
  height: number;
  strength: number;
  sideAbbr?: string;
  pdfSortMain?: PdfPanelMainSortMeta;
  pdfSortDivider?: PdfPanelDividerSortMeta;
  /** Order parts first appeared in build map (min when bucket merges). */
  sortStableSeq?: number;
}

/**
 * Create or update a panel entry in the panels map
 */
function addOrUpdatePanel(options: AddPanelOptions): void {
  const {
    panels,
    key,
    props,
    colorName,
    width,
    height,
    strength,
    sideAbbr,
    pdfSortMain,
    pdfSortDivider,
    sortStableSeq,
  } = options;

  const existing = panels.get(key);
  if (existing) {
    existing.countNum += 1;
    existing.count = existing.countNum.toString();
    // Collect unique sides
    if (sideAbbr && existing.sides && !existing.sides.includes(sideAbbr)) {
      existing.sides.push(sideAbbr);
      existing.side = existing.sides.join(", ");
    }
    if (pdfSortMain) {
      existing.pdfSortMain = mergePdfSortMain(
        existing.pdfSortMain,
        pdfSortMain,
      );
    }
    if (pdfSortDivider) {
      existing.pdfSortDivider = mergePdfSortDivider(
        existing.pdfSortDivider,
        pdfSortDivider,
      );
    }
    if (sortStableSeq !== undefined) {
      existing.pdfSortStable = Math.min(
        existing.pdfSortStable ?? sortStableSeq,
        sortStableSeq,
      );
    }
    return;
  }

  panels.set(key, {
    countNum: 1,
    count: "1",
    width: `${Math.round(width)}`,
    height: `${Math.round(height)}`,
    color: colorName,
    group: props.group ?? "",
    strength: strength > 0 ? `${strength}` : "",
    note: props.note ?? "",
    side: sideAbbr ?? "",
    sides: sideAbbr ? [sideAbbr] : [],
    pdfSortMain,
    pdfSortDivider,
    pdfSortStable: sortStableSeq,
  });
}

/**
 * Derive side abbreviation from position (for underlays)
 * Negative x = left (li), positive x = right (re)
 */
function getSideFromPosition(position: { x: number } | undefined): string {
  if (!position) return "";
  // Use x position to determine left/right
  if (position.x < 0) return "li";
  if (position.x > 0) return "re";
  return "";
}

/**
 * Main PDF Holzplatten table: legacy used `Panel` + panelType SlideRail; builds now use
 * {@link LTPartType.SlideRail}. Aggregate Panel + SlideRail like old planInfo.parts[Panel].
 */
function collectMainHolzplattenPanels(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
): PdfPanelEntry[] {
  const panels: Map<
    string,
    PdfPanelEntry & { countNum: number; sides?: string[] }
  > = new Map();
  let stableSeq = 0;

  for (const [, part] of partDefinitions) {
    if (part.type === LTPartType.Panel) {
      const props = part as unknown as {
        size?: { x: number; y: number; z?: number };
        color?: unknown;
        group?: string;
        strength?: number;
        note?: string;
      };

      if (part.buildstepName.includes("Rack19Interior")) continue;

      const colorName = getColorName(props.color);
      const width = props.size?.x ?? 0;
      const height = props.size?.z ?? props.size?.y ?? 0;
      const strength = props.strength ?? 0;
      const key = `${strength}-${width}-${height}-${colorName}`;
      stableSeq += 1;
      addOrUpdatePanel({
        panels,
        key,
        props: { group: props.group, note: props.note },
        colorName,
        width,
        height,
        strength,
        pdfSortMain: getMainPanelPdfSort(part),
        sortStableSeq: stableSeq,
      });
      continue;
    }

    if (part.type === LTPartType.SlideRail) {
      const sr = part as unknown as {
        size?: { x: number; y: number };
        partSelection?: { strength: number };
        group?: string;
      };
      const width = sr.size?.x ?? 0;
      const height = sr.size?.y ?? 0;
      const strength = sr.partSelection?.strength ?? 0;
      const colorName = "";
      const key = `${strength}-${width}-${height}-${colorName}`;
      stableSeq += 1;
      addOrUpdatePanel({
        panels,
        key,
        props: { group: sr.group },
        colorName,
        width,
        height,
        strength,
        pdfSortMain: { overlaps: -1, isCorpus: 0 },
        sortStableSeq: stableSeq,
      });
    }
  }

  return Array.from(panels.values()).map(
    ({ countNum, sides, ...rest }) => rest,
  );
}

/**
 * Collect panel data from part definitions
 * @param sideFilter - Optional filter:
 *   - "underlay": only BOTTOM side (for case underlays, not for rack)
 *   - "divider": all TrayPanels that are dividers (non-BOTTOM for case, all for rack)
 * @param interiorType - The interior type (CASE or RACK) for filtering logic
 */
function collectPanels(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
  partType: LTPartType,
  sideFilter?: "underlay" | "divider",
  interiorType?: string,
): PdfPanelEntry[] {
  const panels: Map<
    string,
    PdfPanelEntry & { countNum: number; sides?: string[] }
  > = new Map();

  for (const [, part] of partDefinitions) {
    if (part.type !== partType) continue;

    const props = part as unknown as {
      size?: { x: number; y: number; z?: number };
      color?: unknown;
      group?: string;
      strength?: number;
      note?: string;
      side?: number; // Sides enum value
      position?: Point3D;
    };

    if (shouldFilterPanel(sideFilter, interiorType, props.side)) continue;

    const { buildstepName } = part;

    // Exclude rack rail underlays from outer panels collection (by buildstepName)
    if (
      partType === LTPartType.Panel &&
      buildstepName.includes("Rack19Interior")
    ) {
      continue;
    }

    const colorName = getColorName(props.color);
    const width = props.size?.x ?? 0;
    const height = props.size?.z ?? props.size?.y ?? 0;
    const strength = props.strength ?? 0;

    // For underlays, derive side from position (left/right based on x)
    const sideAbbr =
      sideFilter === "underlay"
        ? getSideFromPosition(props.position)
        : undefined;

    // Aggregate panels by dimensions, strength, and color only (not by group)
    const key = `${strength}-${width}-${height}-${colorName}`;
    const pdfSortMain =
      partType === LTPartType.Panel && sideFilter === undefined
        ? getMainPanelPdfSort(part)
        : undefined;
    const pdfSortDivider =
      partType === LTPartType.TrayPanel && sideFilter === "divider"
        ? getDividerPdfSortFromTray(part)
        : undefined;
    addOrUpdatePanel({
      panels,
      key,
      props: { group: props.group, note: props.note },
      colorName,
      width,
      height,
      strength,
      sideAbbr,
      pdfSortMain,
      pdfSortDivider,
    });
  }

  // Remove internal tracking fields before returning
  return Array.from(panels.values()).map(
    ({ countNum, sides, ...rest }) => rest,
  );
}

/**
 * Update existing underlay entry with new side info
 */
function updateUnderlayEntry(
  existing: PdfPanelEntry & { countNum: number; sides?: string[] },
  sideAbbr: string,
): void {
  existing.countNum += 1;
  existing.count = existing.countNum.toString();
  if (sideAbbr && existing.sides && !existing.sides.includes(sideAbbr)) {
    existing.sides.push(sideAbbr);
    existing.side = existing.sides.join(", ");
  }
}

/**
 * Create new underlay entry
 */
function createUnderlayEntry(
  width: number,
  height: number,
  colorName: string,
  group: string,
  strength: number,
  note: string,
  sideAbbr: string,
): PdfPanelEntry & { countNum: number; sides?: string[] } {
  const length = Math.max(width, height);
  return {
    countNum: 1,
    count: "1",
    width: `${Math.round(length)}`,
    height: `${Math.round(Math.min(width, height))}`,
    color: colorName,
    group,
    strength: strength > 0 ? `${strength}` : "",
    note,
    side: sideAbbr,
    sides: sideAbbr ? [sideAbbr] : [],
  };
}

/**
 * Collect rack rail underlays from part definitions
 * These are LTPartType.Panel parts created by Rack19InteriorBuildStep
 */
function collectRackRailUnderlays(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
): PdfPanelEntry[] {
  const underlays: Map<
    string,
    PdfPanelEntry & { countNum: number; sides?: string[] }
  > = new Map();

  for (const [, part] of partDefinitions) {
    if (part.type !== LTPartType.Panel) continue;
    if (!part.buildstepName.includes("Rack19Interior")) continue;

    const props = part as unknown as {
      size?: { x: number; y: number; z?: number };
      color?: unknown;
      group?: string;
      strength?: number;
      note?: string;
      side?: number;
      position?: Point3D;
    };

    const colorName = getColorName(props.color);
    const width = props.size?.x ?? 0;
    const height = props.size?.z ?? props.size?.y ?? 0;
    const strength = props.strength ?? 0;
    const sideAbbr = getSideFromPosition(props.position);
    const length = Math.max(width, height);
    const key = `${strength}-${length}`;
    const dividerSort = getDividerPdfSortFromRackUnderlay(part);

    const existing = underlays.get(key);
    if (existing) {
      updateUnderlayEntry(existing, sideAbbr);
      existing.pdfSortDivider = mergePdfSortDivider(
        existing.pdfSortDivider,
        dividerSort,
      );
    } else {
      const entry = createUnderlayEntry(
        width,
        height,
        colorName,
        props.group ?? "",
        strength,
        props.note ?? "",
        sideAbbr,
      );
      entry.pdfSortDivider = dividerSort;
      underlays.set(key, entry);
    }
  }

  return Array.from(underlays.values()).map(
    ({ countNum, sides, ...rest }) => rest,
  );
}

/**
 * Collect underlay panels for 2D preview visualization
 * Returns full 3D position/size data for each panel
 */
function collectUnderlayPreviews(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
): import("./rackRailPreviewSchema").UnderlayPreviewEntry[] {
  const underlayPreviews: import("./rackRailPreviewSchema").UnderlayPreviewEntry[] =
    [];

  for (const [, part] of partDefinitions) {
    if (part.type !== LTPartType.Panel) continue;
    // Only collect panels from Rack19InteriorBuildStep (rack rail underlays)
    if (!part.buildstepName.includes("Rack19Interior")) continue;

    const props = part as unknown as {
      size?: { x: number; y: number };
      strength?: number;
      side?: number;
      position?: Point3D;
      orientation?: number;
      color?: unknown;
    };

    if (!props.position || !props.size) continue;

    // Determine side from position (negative x = LEFT, positive x = RIGHT)
    const side = (props.position.x ?? 0) < 0 ? Sides.LEFT : Sides.RIGHT;

    // Extract color from panel material
    const color = extractColorFromPanelColor(props.color);

    underlayPreviews.push({
      position: {
        x: props.position.x ?? 0,
        y: props.position.y ?? 0,
        z: props.position.z ?? 0,
      },
      size: {
        x: props.size.x ?? 0,
        y: props.size.y ?? 0,
      },
      side: props.side ?? side,
      strength: props.strength ?? 0,
      orientation: props.orientation,
      color,
    });
  }

  return underlayPreviews;
}

/**
 * Get or create a group for a given depth
 */
function getOrCreateGroup(
  groups: Map<
    string,
    {
      depth: string;
      description: string;
      entries: Map<string, PdfAngleSectionEntry & { countNum: number }>;
    }
  >,
  depth: string,
): {
  depth: string;
  description: string;
  entries: Map<string, PdfAngleSectionEntry & { countNum: number }>;
} {
  let group = groups.get(depth);
  if (!group) {
    group = { depth, description: "", entries: new Map() };
    groups.set(depth, group);
  }
  return group;
}

/**
 * Add or update an angle section entry
 */
function addOrUpdateAngleSectionEntry(
  group: {
    depth: string;
    description: string;
    entries: Map<string, PdfAngleSectionEntry & { countNum: number }>;
  },
  entryKey: string,
  props: {
    length?: number;
    dir?: "x" | "y" | "z";
    cutSign?: string;
    offset?: number;
    cuts?: {
      left: {
        bottom: boolean;
        top: boolean;
      };
      right: {
        bottom: boolean;
        top: boolean;
      };
    };
  },
  optionDescription?: string,
): void {
  if (optionDescription && !group.description) {
    group.description = optionDescription;
  }
  if (incrementEntryCount(group.entries, entryKey)) return;

  // Normalize cuts so 180° rotated cuts are treated as identical
  const normalizedCuts = props.cuts ? normalizeCuts(props.cuts) : undefined;
  const cutSign =
    normalizedCuts === undefined
      ? (props.cutSign ?? "")
      : getAngleSectionCutSign(normalizedCuts);
  group.entries.set(entryKey, {
    countNum: 1,
    count: "1",
    length: `${Math.round(props.length ?? 0)}`,
    direction: props.dir ?? "x",
    cutSign,
    offset: props.offset ? `${Math.round(props.offset)}` : "",
  });
}

/**
 * Collect angle section data from part definitions
 */
function collectAngleSections(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
): PdfAngleSectionGroup[] {
  const groups: Map<
    string,
    {
      depth: string;
      description: string;
      entries: Map<string, PdfAngleSectionEntry & { countNum: number }>;
    }
  > = new Map();

  for (const [, part] of partDefinitions) {
    if (part.type !== LTPartType.AngleSection) continue;

    const props = part as unknown as {
      partSelection?: { depth?: number; description?: string };
      length?: number;
      dir?: "x" | "y" | "z";
      cutSign?: string;
      offset?: number;
      cuts?: {
        left: {
          bottom: boolean;
          top: boolean;
        };
        right: {
          bottom: boolean;
          top: boolean;
        };
      };
    };

    // Use partSelection.depth (resolved Korpus/Deckel type); fallback for legacy data
    const depthNum =
      props.partSelection?.depth ??
      (part as unknown as { depth?: number }).depth;
    const depth = `${Math.round(depthNum ?? 0)}`;
    const optionDescription = props.partSelection?.description ?? "";
    // Include cuts in key to group entries with same cuts together
    // Normalize cuts to treat 180° rotated cuts as identical
    const cutsKey = props.cuts ? normalizeCutsKey(props.cuts) : "0000";
    const cutSignForKey = props.cuts
      ? getAngleSectionCutSign(normalizeCuts(props.cuts))
      : (props.cutSign ?? "");
    const entryKey = `${props.length}-${props.dir}-${cutSignForKey}-${props.offset}-${cutsKey}`;

    const group = getOrCreateGroup(groups, depth);
    addOrUpdateAngleSectionEntry(group, entryKey, props, optionDescription);
  }

  return Array.from(groups.values()).map((g) => ({
    depth: g.depth,
    description: g.description || g.depth,
    entries: Array.from(g.entries.values()).map(
      ({ countNum, ...rest }) => rest,
    ),
  }));
}

/**
 * Extract pad size from different formats
 * Handles array [width, depth], object {x, y, z}, or direct width/depth properties
 */
function extractPadSize(
  size: [number, number] | { x: number; y: number; z?: number } | undefined,
  fallbackWidth?: number,
  fallbackDepth?: number,
): { width: number; depth: number } {
  if (!size) {
    return { width: fallbackWidth ?? 0, depth: fallbackDepth ?? 0 };
  }
  if (Array.isArray(size)) {
    return { width: size[0] ?? 0, depth: size[1] ?? 0 };
  }
  return { width: size.x ?? 0, depth: size.y ?? size.z ?? 0 };
}

/**
 * Add or update a padding entry in the paddings map
 */
function addOrUpdatePadding(
  paddings: Map<string, PdfPaddingEntry & { countNum: number }>,
  key: string,
  props: {
    description?: string;
    style?: string;
  },
  width: number,
  depth: number,
  thickness: number,
  sideStr: string,
): void {
  if (incrementEntryCount(paddings, key)) return;

  paddings.set(key, {
    countNum: 1,
    count: "1",
    width: `${Math.round(width)}`, // B (Breite)
    height: `${Math.round(depth)}`, // H (Höhe/Tiefe)
    depth: `${Math.round(thickness)}`, // Dicke (already in mm, not /10)
    description: props.description ?? "",
    side: sideStr,
    strength: thickness ? `${thickness}` : "",
    style: props.style ?? "",
  });
}

/**
 * Collect padding data from part definitions
 */
function collectPaddings(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
): PdfPaddingEntry[] {
  const paddings: Map<string, PdfPaddingEntry & { countNum: number }> =
    new Map();

  for (const [, part] of partDefinitions) {
    if (part.type !== LTPartType.Pad) continue;

    const props = part as PadDefinition;

    const { width, depth } = extractPadSize(props.size);
    const thickness = props.strength;
    const sideStr = String(props.side);
    const key = `${width}-${depth}-${thickness}-${sideStr}-${props.style}`;

    addOrUpdatePadding(paddings, key, props, width, depth, thickness, sideStr);
  }

  return removeCountNumFromMapValues(paddings);
}

/**
 * Collect supply parts (Stückliste)
 */
export function collectSupplyParts(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
): PdfSupplyPartEntry[] {
  const parts: Map<string, PdfSupplyPartEntry & { countNum: number }> =
    new Map();

  for (const [, part] of partDefinitions) {
    const props = part as unknown as PartLike;

    if (!props.isSupplyPart) continue;
    if (isPartHiddenFromPlan(props)) continue;
    // Same gate as the price list: an Ausschnitt is a hole, not a purchase part.
    if (isArticleFreePart(props)) continue;

    // Get description from partSelection first (e.g., "Kugelecke mittel (L-Ecke)")
    // Fall back to props.description, then part.type
    const description =
      props.partSelection?.description ??
      (props.description as string | undefined) ??
      part.type;

    // Get article number from partSelection.name (e.g., "ECK4043") or articleNumber
    const articleNumber =
      props.partSelection?.name ??
      (props.partSelection?.articleNumber as string | undefined) ??
      (props.articleNumber as string | undefined) ??
      "";

    const key = `${articleNumber}-${description}`;
    const existing = parts.get(key);

    if (existing) {
      existing.countNum += 1;
      existing.count = existing.countNum.toString();
    } else {
      parts.set(key, {
        countNum: 1,
        count: "1",
        articleNumber,
        description,
      });
    }
  }

  return removeCountNumFromMapValues(parts);
}

/**
 * Convert miter type enum to German label
 */
function getMiterLabel(miter: number): string {
  // MiterType: NONE=0, START=1, END=2, BOTH=3
  switch (miter) {
    case 1:
      return "Anfang";
    case 2:
      return "Ende";
    case 3:
      return "Beides";
    default:
      return "-";
  }
}

/**
 * Calculate length from an array of 3D points (in mm)
 * @param points Array of points with x, y, z coordinates
 */
function calculatePointsLength(points: Point3D[]): number {
  if (!points || points.length < 2) return 0;

  let totalLength = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    if (p1 && p2) {
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const dz = p2.z - p1.z;
      totalLength += Math.hypot(dx, dy, dz);
    }
  }
  return totalLength;
}

/**
 * Convert points to serializable format (array of [x, y, z] tuples)
 */
function convertPointsToTuples(
  points: Point3DOrTuple[] | undefined,
): Array<[number, number, number]> | undefined {
  if (!points) return undefined;

  return points.map((p): [number, number, number] =>
    Array.isArray(p) ? p : [p.x, p.y, p.z],
  );
}

/**
 * Create a profile entry from part properties
 */
function createProfileEntry(props: {
  points?: Point3D[];
  miter?: number;
  inverse?: boolean;
  cutReference?: number;
  partSelection?: { description?: string; crossSection?: string };
}): PdfProfileEntry {
  const length = calculatePointsLength(props.points ?? []);
  const miterLabel = getMiterLabel(props.miter ?? 0);
  const position = props.inverse ? "Unten" : "Oben";
  const pointsArray = convertPointsToTuples(props.points);

  const entry: PdfProfileEntry = {
    count: "1",
    length: `${Math.round(length)}`,
    miterType: miterLabel,
    position,
  };

  if (props.partSelection?.crossSection) {
    entry.crossSection = props.partSelection.crossSection;
  }
  if (pointsArray && pointsArray.length > 0) {
    entry.points = pointsArray;
  }
  if (props.cutReference !== undefined) {
    entry.cutReference = props.cutReference;
  }

  return entry;
}

/**
 * Collect closing profiles (Schließprofile) - each profile is rendered individually
 */
function collectProfiles(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
): { profiles: PdfProfileEntry[]; description: string } {
  const profiles: PdfProfileEntry[] = [];
  let profileDescription = "";

  for (const [, part] of partDefinitions) {
    if (part.type !== LTPartType.Profile) continue;

    const props = part as unknown as {
      points?: Point3D[];
      miter?: number;
      inverse?: boolean;
      cutReference?: number;
      partSelection?: { description?: string; crossSection?: string };
      group?: string;
    };

    if (!profileDescription && props.partSelection?.description) {
      profileDescription = props.partSelection.description;
    }

    profiles.push(createProfileEntry(props));
  }

  return { profiles, description: profileDescription };
}

/**
 * Collect rack rails from part definitions for table display
 */
function collectRackRails(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
): { rackRails: PdfRackRailEntry[] } {
  const rackRails: PdfRackRailEntry[] = [];

  // Group by length and side for table display
  const grouped = new Map<
    string,
    {
      count: number;
      length: number;
      side: number;
      symbol: string;
    }
  >();

  for (const [, part] of partDefinitions) {
    if (part.type !== LTPartType.RackRail) continue;

    const props = part as unknown as {
      points?: Point3D[];
      partSelection?: { spacing?: number; symbol?: string };
      position?: Point3D;
    };

    const length = calculatePointsLength(props.points ?? []);
    // Determine mounting side from position - negative x = LEFT (0), positive x = RIGHT (1)
    const positionX = props.position?.x ?? 0;
    const side = positionX < 0 ? 0 : 1; // 0 = LEFT, 1 = RIGHT
    const symbol = props.partSelection?.symbol ?? "";

    // Group key for table display
    const key = `${Math.round(length)}-${side}-${symbol}`;

    const existing = grouped.get(key);
    if (existing) {
      existing.count++;
    } else {
      grouped.set(key, {
        count: 1,
        length,
        side,
        symbol,
      });
    }
  }

  // Convert grouped data to rack rail entries for table
  for (const [, data] of grouped) {
    const positionMap: Record<number, string> = {
      0: "Links",
      1: "Rechts",
      2: "Hinten",
      3: "Vorne",
      4: "Unten",
      5: "Oben",
    };

    rackRails.push({
      count: String(data.count),
      length: `${Math.round(data.length)}`,
      position: positionMap[data.side] ?? "",
      symbol: data.symbol,
    });
  }

  return { rackRails };
}

// =============================================================================
// TABLE FORMATTERS
// =============================================================================

/**
 * Ensure the framedText cell renderer is registered for table rendering
 * This function is idempotent - multiple calls will re-register but have no effect
 */
async function ensureFramedTextRendererRegistered(): Promise<void> {
  registerCellRenderer(
    "framedText",
    async (
      value: unknown,
      cellX: number,
      cellY: number,
      cellWidth: number,
      cellHeight: number,
      context: CellRenderContext,
    ) => {
      const { renderFramedText } = await import("./framedTextCell");
      renderFramedText(
        value as FramedTextValue,
        { x: cellX, y: cellY, width: cellWidth, height: cellHeight },
        context,
      );
    },
  );
}

/**
 * Format panels as TableData for the general table component
 */
function formatPanelsTable(panels: PdfPanelEntry[]): string {
  if (panels.length === 0) {
    return JSON.stringify({
      columns: [
        { name: "Anz", widthPct: 0.12, align: Align.Center }, // Comment: Anzahl column
        { name: "X", widthPct: 0.08, align: Align.Center }, // Comment: Separator between Anz and B
        { name: "B (mm)", widthPct: 0.28, align: Align.Center }, // Comment: Breite column
        { name: "X", widthPct: 0.08, align: Align.Center }, // Comment: Separator between B and H
        { name: "H (mm)", widthPct: 0.44, align: Align.Center }, // Comment: Höhe column
      ],
      rows: [],
      rowHeight: 7,
      fontSize: { header: PDF_TITLE_SIZE, body: PDF_TEXT_SIZE }, // Use central font size definitions
      showHeader: false,
    });
  }

  // Legacy Holzplatten order: overlaps desc, corpus flag desc, then area desc
  const sortedPanels = [...panels].sort(comparePdfMainPanelsLegacy);

  const tableData: TableData = {
    columns: [
      { name: "Anz", widthPct: 0.12, align: Align.Center }, // Comment: Anzahl column
      { name: "X", widthPct: 0.08, align: Align.Center }, // Comment: Separator between Anz and B
      { name: "B (mm)", widthPct: 0.28, align: Align.Center }, // Comment: Breite column
      { name: "X", widthPct: 0.08, align: Align.Center }, // Comment: Separator between B and H
      { name: "H (mm)", widthPct: 0.44, align: Align.Center }, // Comment: Höhe column
    ],
    rows: sortedPanels.map((p) => [
      p.count, // Anz
      "×", // X separator
      formatMm(p.width), // B (mm)
      "×", // X separator
      formatMm(p.height), // H (mm)
    ]),
    rowHeight: 7,
    fontSize: { header: 15, body: 12 }, // Increased by ~50% for better readability
    showHeader: false, // Hide header row
  };
  return JSON.stringify(tableData);
}

/**
 * Format underlays (rack rail shims) as TableData with strength in a framed box at the beginning
 * Format: [St] | Anz | × | Länge | Seite (e.g., "9 | 4 | × | 455 | li, re")
 */
async function formatUnderlaysTable(
  underlays: PdfPanelEntry[],
): Promise<string> {
  await ensureFramedTextRendererRegistered();

  const tableData: TableData = {
    columns: [
      // St (mm) - Stärke with framed text renderer
      {
        name: "St", // Comment: Stärke column
        widthPct: 0.12,
        align: Align.Center,
        rendererType: "framedText",
      } satisfies TableColumn,
      // Anz - Anzahl
      { name: "Anz", widthPct: 0.1, align: Align.Center }, // Comment: Anzahl column
      // X separator
      { name: "X", widthPct: 0.06, align: Align.Center }, // Comment: Separator
      // Länge (mm) - Length (the longer dimension)
      { name: "Länge", widthPct: 0.18, align: Align.Center }, // Comment: Länge column
      // X separator for Breite
      { name: "X2", widthPct: 0.06, align: Align.Center }, // Comment: Separator
      // Breite (mm) - Width (the shorter dimension)
      { name: "Breite", widthPct: 0.18, align: Align.Center }, // Comment: Breite column
      // Seite - Combined sides
      { name: "Seite", widthPct: 0.3, align: Align.Center }, // Comment: Seite column
    ],
    rows: [...underlays].sort(comparePdfDividerLegacy).map((u) => [
      u.strength || "", // St (mm) - in framed box
      u.count, // Anz
      "×", // X separator
      formatMm(u.width), // Länge (the longer dimension)
      "×", // X separator
      formatMm(u.height), // Breite (the shorter dimension)
      u.side || "", // Seite (li, re, etc.)
    ]),
    rowHeight: 7,
    fontSize: { header: 15, body: 12 }, // Increased by ~50% for better readability
    showHeader: false, // Hide header row
  };
  return JSON.stringify(tableData);
}

/**
 * Format rack rails as TableData with type in framed box at the beginning
 * Format: [Typ] | Anz | × | Länge (e.g., "[Eco2] | 4 | × | 455")
 */
async function formatRackRailsTable(
  rackRails: PdfRackRailEntry[],
): Promise<string> {
  await ensureFramedTextRendererRegistered();

  // Group rack rails by symbol AND length
  const grouped = new Map<
    string,
    {
      symbol: string;
      count: number;
      length: string;
    }
  >();

  for (const r of rackRails) {
    const length = formatMm(r.length);
    const count = parseInt(r.count, 10) || 0;
    const symbol = r.symbol || "";
    const key = `${symbol}-${length}`;

    const existing = grouped.get(key);
    if (existing) {
      existing.count += count;
    } else {
      grouped.set(key, {
        symbol,
        count,
        length,
      });
    }
  }

  // Convert grouped data to rows
  const rows = Array.from(grouped.values()).map((group) => [
    group.symbol, // Typ - in framed box
    group.count.toString(), // Anz
    "×", // X separator
    group.length, // Länge
  ]);

  const tableData: TableData = {
    columns: [
      // Typ - with framed text renderer
      {
        name: "Typ",
        widthPct: 0.25,
        align: Align.Center,
        rendererType: "framedText",
      } satisfies TableColumn,
      // Anz - Anzahl
      { name: "Anz", widthPct: 0.15, align: Align.Center }, // Comment: Anzahl column
      // X separator
      { name: "X", widthPct: 0.1, align: Align.Center }, // Comment: Separator
      // Länge (mm) - Length
      { name: "Länge", widthPct: 0.5, align: Align.Center }, // Comment: Länge column
    ],
    rows,
    rowHeight: 7,
    fontSize: { header: 15, body: 12 }, // Increased by ~50% for better readability
    showHeader: false, // Hide header row
  };
  return JSON.stringify(tableData);
}

/**
 * Format a value as mm (integer)
 */
function formatMm(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num)) return "0";
  return Math.round(num).toString();
}

/**
 * Format angle sections as TableData for the general table component
 */
function formatAngleSectionsTable(groups: PdfAngleSectionGroup[]): string {
  const rows: unknown[][] = [];
  for (const group of groups) {
    const byLengthDesc = [...group.entries].sort(
      (a, b) => (parseFloat(b.length) || 0) - (parseFloat(a.length) || 0),
    );
    for (const entry of byLengthDesc) {
      const lengthNum = parseFloat(entry.length) || 0;
      const rivetSpacing = calculateRivetSpacing(lengthNum);

      rows.push([
        entry.count,
        "×",
        formatMm(entry.length),
        rivetSpacing,
        entry.cutSign || "",
      ]);
    }
  }

  const tableData: TableData = {
    columns: [
      { name: "Anz", widthPct: 0.12, align: Align.Center },
      { name: "X", widthPct: 0.06, align: Align.Center },
      { name: "Länge (mm)", widthPct: 0.28, align: Align.Center },
      { name: "Nietenabstand", widthPct: 0.28, align: Align.Center },
      { name: "Zeichen", widthPct: 0.26, align: Align.Center },
    ],
    rows,
    rowHeight: 7,
    fontSize: { header: 15, body: 12 },
    showHeader: false,
  };
  return JSON.stringify(tableData);
}

const SIDE_ABBREV_BY_ENGLISH = {
  front: SidesNames[Sides.FRONT],
  back: SidesNames[Sides.BACK],
  left: SidesNames[Sides.LEFT],
  right: SidesNames[Sides.RIGHT],
  bottom: SidesNames[Sides.BOTTOM],
  top: SidesNames[Sides.TOP],
} as const;

type SideEnglishKeyword = keyof typeof SIDE_ABBREV_BY_ENGLISH;

function isSideEnglishKeyword(s: string): s is SideEnglishKeyword {
  return Object.hasOwn(SIDE_ABBREV_BY_ENGLISH, s);
}

/**
 * Convert side name/number to German abbreviation (matches {@link Sides} + {@link SidesNames})
 */
function getSideAbbreviation(side: string | number | undefined): string {
  if (side === undefined || side === null) return "";

  const key = String(side).trim().toLowerCase();
  if (isSideEnglishKeyword(key)) {
    return SIDE_ABBREV_BY_ENGLISH[key];
  }

  const n = Number(side);
  if (
    Number.isFinite(n) &&
    Number.isInteger(n) &&
    n >= Sides.LEFT &&
    n <= Sides.TOP
  ) {
    return SidesNames[n as Sides];
  }

  return key.substring(0, 2);
}

/**
 * Schaumstoff-Zeilenreihenfolge wie Legacy-PDF (`sortBy` auf Seiten-String).
 * @see oldcode/frontend.txt — paddingList, gitlab …/reactend/issues/69
 */
function getPaddingSidesSortPriority(commaJoinedSides: string): number {
  const s = commaJoinedSides.trim();
  if (s.startsWith("hi") || s.startsWith("vo")) return 0;
  if (s.startsWith("li") || s.startsWith("re")) return 1;
  return 2;
}

/** Eindeutige Seitenkürzel alphabetisch wie im alten `sidesRaw.sort()` */
function formatPaddingsSidesCell(sides: string[]): string {
  return [...new Set(sides)]
    .sort((a, b) => a.localeCompare(b, "de"))
    .join(", ");
}

/**
 * Get type abbreviation for padding style
 */
function getPaddingTypeAbbreviation(style?: string): string {
  if (!style) return "";
  const styleLower = style.toLowerCase();
  // Standard type: don't show anything
  if (styleLower === "standard") {
    return "";
  }
  if (styleLower === "eggbox") {
    return "N"; // Noppen
  }
  if (styleLower === "soft") {
    return "W"; // Weich
  }
  // Fallback: capitalize first letter
  return style.charAt(0).toUpperCase() + style.slice(1).toLowerCase();
}

/**
 * Format paddings as TableData for the general table component
 * Groups identical paddings (same Typ, B, H, Dicke) and combines their sides
 */
async function formatPaddingsTable(
  paddings: PdfPaddingEntry[],
): Promise<string> {
  // Import and register the framed text renderer for type column
  await ensureFramedTextRendererRegistered();

  // Group paddings by type and dimensions (style, width, height, strength/depth)
  const grouped = new Map<
    string,
    {
      type: string;
      sides: string[];
      count: number;
      width: string;
      height: string;
      thickness: string;
    }
  >();

  for (const p of paddings) {
    const width = formatMm(p.width);
    const height = formatMm(p.height);
    const thickness = p.strength || p.depth || "";
    const count = parseInt(p.count, 10) || 0;
    const typeAbbr = getPaddingTypeAbbreviation(p.style);
    // Use original style for grouping to ensure correct grouping by type
    // The abbreviation is only used for display
    const styleForGrouping = (p.style || "").toLowerCase();

    // Create a key from type (original style) and dimensions
    const key = `${styleForGrouping}|${width}|${height}|${thickness}`;

    const existing = grouped.get(key);
    if (existing) {
      existing.sides.push(getSideAbbreviation(p.side));
      existing.count += count;
    } else {
      grouped.set(key, {
        type: typeAbbr,
        sides: [getSideAbbreviation(p.side)],
        count,
        width,
        height,
        thickness,
      });
    }
  }

  // Legacy order: vo/hi first, then li/re, then other sides (stable tie-break = map insertion order)
  const sortedGroups = [...grouped.values()].sort((a, b) => {
    const priA = getPaddingSidesSortPriority(formatPaddingsSidesCell(a.sides));
    const priB = getPaddingSidesSortPriority(formatPaddingsSidesCell(b.sides));
    return priA - priB;
  });

  const rows = sortedGroups.map((group) => [
    formatPaddingsSidesCell(group.sides),
    group.count.toString(), // Anz
    "×", // X separator
    group.width, // B (mm)
    "×", // X separator
    group.height, // H (mm)
    group.thickness, // Dicke (mm)
    group.type, // Typ - in framed box (last column)
  ]);

  const tableData: TableData = {
    columns: [
      // Seite
      { name: "Seite", widthPct: 0.14, align: Align.Left }, // Comment: Seite column
      // Anz
      { name: "Anz", widthPct: 0.18, align: Align.Center }, // Comment: Anzahl column
      // X separator
      { name: "X", widthPct: 0.06, align: Align.Center }, // Comment: Separator between Anz and B
      // B (mm)
      { name: "B (mm)", widthPct: 0.19, align: Align.Center }, // Comment: Breite column
      // X separator
      { name: "X", widthPct: 0.06, align: Align.Center }, // Comment: Separator between B and H
      // H (mm)
      { name: "H (mm)", widthPct: 0.19, align: Align.Center }, // Comment: Höhe column
      // Dicke (mm)
      { name: "Dicke (mm)", widthPct: 0.1, align: Align.Center }, // Comment: Dicke column
      // Typ - framed [N] etc. — kept narrow (short abbreviations)
      {
        name: "Typ",
        widthPct: 0.08,
        align: Align.Center,
        rendererType: "framedText",
      } satisfies TableColumn,
    ],
    rows,
    rowHeight: 7,
    fontSize: { header: 15, body: 12 }, // Increased by ~50% for better readability
    showHeader: false, // Hide header row
  };
  return JSON.stringify(tableData);
}

/**
 * Format dividers as TableData for the general table component
 */
async function formatDividersTable(dividers: PdfPanelEntry[]): Promise<string> {
  await ensureFramedTextRendererRegistered();

  const tableData: TableData = {
    columns: [
      // St (mm) - Stärke with framed text renderer
      {
        name: "St (mm)", // Comment: Stärke column
        widthPct: 0.15,
        align: Align.Center,
        rendererType: "framedText",
      } satisfies TableColumn,
      // Anz - Anzahl
      { name: "Anz", widthPct: 0.12, align: Align.Center }, // Comment: Anzahl column
      // X separator
      { name: "X", widthPct: 0.08, align: Align.Center }, // Comment: Separator between Anz and B
      // B (mm) - Breite
      { name: "B (mm)", widthPct: 0.22, align: Align.Center }, // Comment: Breite column
      // X separator
      { name: "X", widthPct: 0.08, align: Align.Center }, // Comment: Separator between B and H
      // H (mm) - Höhe
      { name: "H (mm)", widthPct: 0.35, align: Align.Center }, // Comment: Höhe column
    ],
    rows: [...dividers].sort(comparePdfDividerLegacy).map((d) => [
      d.strength || "", // St (mm)
      d.count, // Anz
      "×", // X separator
      formatMm(d.width), // B (mm)
      "×", // X separator
      formatMm(d.height), // H (mm)
    ]),
    rowHeight: 7,
    fontSize: { header: 15, body: 12 }, // Increased by ~50% for better readability
    showHeader: false, // Hide header row
  };
  return JSON.stringify(tableData);
}

/**
 * Format profiles as pdfme table JSON: [[count, length (mm), miterType, position], ...]
 */
function formatProfilesTable(profiles: PdfProfileEntry[]): string {
  if (profiles.length === 0) return JSON.stringify([]);
  // Return the full objects for the custom schema to consume
  return JSON.stringify(profiles);
}

// =============================================================================
// MAIN DATA COLLECTION
// =============================================================================

/**
 * Collect all PDF data from a product build
 */
export function collectPdfData(
  settings: FlightCaseSettings,
  blueprint: FlightCaseBlueprint,
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
  positionInput: PdfPositionInput,
): PdfInputData {
  const header = collectHeaderData(settings, blueprint, positionInput);
  // Get interior type for filtering logic
  const interiorType = settings.interiorOptions?.type;
  const panels = collectMainHolzplattenPanels(partDefinitions);
  // Dividers: For RACK = all TrayPanels, For CASE = non-BOTTOM TrayPanels
  const panelDividers = collectPanels(
    partDefinitions,
    LTPartType.TrayPanel,
    "divider",
    interiorType,
  );
  // Rack rail underlays are LTPartType.Panel parts with note "Unterlegholz"
  const panelUnderlays = collectRackRailUnderlays(partDefinitions);
  const angleSections = collectAngleSections(partDefinitions);
  const paddings = collectPaddings(partDefinitions);
  const supplyParts = collectSupplyParts(partDefinitions);
  const { profiles, description: profileDescription } =
    collectProfiles(partDefinitions);
  const { rackRails } = collectRackRails(partDefinitions);
  // Collect underlay panels for 2D preview (using PanelData format)
  const underlayPreviews = collectUnderlayPreviews(partDefinitions);
  // Single volume dimensions for B/T/H [mm]: from settings.interiorOptions (CASE tree), not from partDefinitions
  let volumeCount = 0;
  let singleVolumeDimensions: { x: number; y: number; z: number } | undefined;
  const interior = settings.interiorOptions;
  if (interior?.type === InteriorType.CASE) {
    const caseInterior: CaseInteriorOptions = interior;
    const tree = caseInterior.tree;
    if (tree?.children?.length) {
      volumeCount = tree.children.length;
      if (volumeCount === 1) {
        const first = tree.children[0];
        if (first && "item" in first && first.type === NodeType.ITEM) {
          const item = first.item;
          singleVolumeDimensions = {
            x: item.width,
            y: item.height,
            z: item.depth,
          };
        }
      }
    }
  }

  // Get profile gapWidth from settings (used as profile number/title)
  const profileGapWidth = settings.exteriorOptions?.profiles?.gapWidth ?? 0;
  const profileName = profileGapWidth > 0 ? String(profileGapWidth) : "";
  // Get profile strength from settings (for header)
  const profileStrength = settings.exteriorOptions?.profiles?.strength ?? 0;

  return {
    header,
    previewImage: "",
    panels,
    panelDividers,
    panelUnderlays,
    supplyParts,
    angleSections,
    paddings,
    rackRails,
    underlayPreviews,
    profiles,
    profileDescription,
    profileName,
    profileStrength: profileStrength > 0 ? String(profileStrength) : "",
    singleVolumeDimensions,
    volumeCount,
  };
}

// =============================================================================
// PDFME INPUT CONVERSION
// =============================================================================

/**
 * Build product header SVG for the productHeader schema
 * Uses the native SVG support from pdfme
 */
async function buildProductHeaderData(
  data: PdfInputData,
  blueprint: FlightCaseBlueprint,
  positionInput: PdfPositionInput,
  boundingBox?: { x: number; y: number; z: number } | null,
): Promise<string> {
  // B[mm], T[mm], H[mm]: no volumes → blueprint innerDimensions; one volume → its size; multiple volumes → empty
  const inner = blueprint.innerDimensions;
  const outer = blueprint.outerDimensions;
  let objectWidth: string;
  let objectDepth: string;
  let objectHeight: string;
  if (data.volumeCount > 1) {
    objectWidth = "";
    objectDepth = "";
    objectHeight = "";
  } else if (data.singleVolumeDimensions) {
    const dim = data.singleVolumeDimensions;
    objectWidth = Math.round(dim.x).toString();
    objectDepth = Math.round(dim.z).toString();
    objectHeight = Math.round(dim.y).toString();
  } else {
    objectWidth = Math.round(inner.x).toString();
    objectDepth = Math.round(inner.z).toString();
    objectHeight = Math.round(inner.y).toString();
  }
  const outerSize = ""; // CNC field - left empty

  // Use bounding box from MeasurementRenderer (calculated during preview capture)
  // This includes handles, locks, corner protectors, etc.
  // Fallback to outerDimensions if bounding box not provided
  const packSize = boundingBox
    ? `${Math.round(boundingBox.x)} × ${Math.round(boundingBox.z)} × ${Math.round(boundingBox.y)}`
    : `${Math.round(outer.x)} × ${Math.round(outer.z)} × ${Math.round(outer.y)}`;

  // Check if paddings table exists
  const hasPaddings = data.paddings && data.paddings.length > 0;
  const variantText = hasPaddings
    ? `inkl. Schaumstoff ${data.header.variant}`
    : data.header.variant;

  // Build the product header data object
  const productHeaderData = {
    // Top row
    group: positionInput.commodityGroup ?? "", // Commodity group from UI
    sb: data.header.userDisplayName,
    date: data.header.date,
    archive: data.header.articleNumber ?? "", // Archive = Article number from UI

    // Product info
    title: data.header.model, // Product name
    variant: variantText,

    // Right side info
    afsId: data.header.afsNumber,
    orderId: positionInput.orderNumber ?? "", // Order number from UI
    customer: data.header.customer,

    // Specs row
    alukante: data.header.angleSectionType, // BS - Aluminium edge
    profilstaerke: data.profileStrength, // PS - Profile strength from settings.exteriorOptions.profiles.strength
    muster: data.header.pattern, // Muster (Quelle) - Pattern
    price: data.header.price,

    // Parts list
    partsList: data.supplyParts.map((part) => ({
      count: part.count,
      description: part.description,
      articleNumber: part.articleNumber,
    })),

    // Dimensions (in mm)
    objectWidth,
    objectDepth,
    objectHeight,
    packSize,
    outerSize,

    // Bottom row
    caseType: data.header.caseType, // Casetyp (Grundform)
    model: "", // Baureihe (Qualität) - left empty
  };

  // Return JSON string for custom schema (instead of SVG)
  return JSON.stringify(productHeaderData);
}

/**
 * Convert PdfInputData to pdfme input format for Page 1 (Header + Panels + Angle Sections)
 * Layout matches the original PlanTemplate.svg
 */
async function toPage1Inputs(
  data: PdfInputData,
  panelsColor: string,
  panelsStrength: string,
  blueprint: FlightCaseBlueprint,
  positionInput: PdfPositionInput,
  boundingBox?: { x: number; y: number; z: number } | null,
): Promise<Record<string, string | { orderId: string }>> {
  // Build product header data (JSON string for custom schema)
  const productHeader = await buildProductHeaderData(
    data,
    blueprint,
    positionInput,
    boundingBox,
  );

  // Build product header graphics (SVG string without text)
  const { buildProductHeaderSvgGraphicsOnly } = await import("./productHeader");
  const productHeaderGraphics = buildProductHeaderSvgGraphicsOnly(
    JSON.parse(productHeader),
  );

  // Angle sections: one header (depth) + table per distinct depth (e.g. "25", "22")
  const angleSectionInputs: Record<string, string> = {};
  for (let i = 0; i < data.angleSections.length; i++) {
    const group = data.angleSections[i];
    if (!group) continue;
    angleSectionInputs[`angleSectionsHeader_${i}`] =
      group.description || group.depth;
    angleSectionInputs[`angleSectionsTable_${i}`] = formatAngleSectionsTable([
      group,
    ]);
  }

  return {
    ...emptyDruckplanInputs,

    // Product header graphics (SVG without text)
    productHeaderGraphics,
    // Product header text (JSON for custom schema)
    productHeader,

    // Preview
    previewImage: data.previewImage,

    // Panels section (outer panels only)
    // Format: "strength|material" (strength will be framed, material below)
    panelsHeader: `${panelsStrength}|${panelsColor}`,
    panelsTable: formatPanelsTable(data.panels),

    // Underlays section (rack rail shims) - no header, just table with strength in framed box
    underlaysTable: await formatUnderlaysTable(data.panelUnderlays),

    // Angle sections (per-depth headers and tables)
    ...angleSectionInputs,

    // Profiles (Schließprofile)
    profilesHeader: data.profileDescription || data.profileName,
    profilesTable: formatProfilesTable(data.profiles),
    // Profile previews header uses description of the selected profile option
    profilePreviewsHeader: data.profileDescription || data.profileName,
    // Profile previews: convert profiles with points to JSON array for 2D top view
    profilePreviews: (() => {
      const profilesWithPoints = data.profiles.filter(
        (p) => p.points && Array.isArray(p.points) && p.points.length >= 2,
      );
      const profilePreviewsData = profilesWithPoints.map((p) => ({
        points: p.points,
        label: p.position, // "Oben" or "Unten"
        cutReference: p.cutReference,
        crossSection: p.crossSection,
        count: p.count, // Add count for display
      }));
      return JSON.stringify(profilePreviewsData);
    })(),
    // Cross-section SVG path for preview above 3D image (use first profile's crossSection)
    profileCrossSection: (() => {
      const profileWithCrossSection = data.profiles.find((p) => {
        const crossSection = p.crossSection;
        return crossSection && crossSection.trim().length > 0;
      });
      return profileWithCrossSection?.crossSection ?? "";
    })(),
    // Left column profile 2D preview (same data as profilePreviews, different position)
    profilePreviewsLeftHeader: data.profileDescription || data.profileName,
    profilePreviewsLeft: (() => {
      const profilesWithPoints = data.profiles.filter(
        (p) => p.points && Array.isArray(p.points) && p.points.length >= 2,
      );
      const profilePreviewsData = profilesWithPoints.map((p) => ({
        points: p.points,
        label: p.position, // "Oben" or "Unten"
        cutReference: p.cutReference,
        crossSection: p.crossSection,
        count: p.count,
      }));
      return JSON.stringify(profilePreviewsData);
    })(),

    // Rack rails (on page 1 if present) - as proper table with dashed lines
    rackRailsTable: await formatRackRailsTable(data.rackRails),
    rackRailsImage: "",

    // Underlay panel 2D preview (grouped by side)
    rackRailPreview: JSON.stringify(data.underlayPreviews),

    // Footer: same ID as in header (Auftrag#)
    footer: positionInput.orderNumber ?? "",
  };
}

/**
 * Determine the viewing side based on opening side
 * Checks all cuts - if reference side is LEFT, RIGHT, FRONT or BACK, uses it
 * Otherwise defaults to TOP
 * BOTTOM is converted to TOP since bottom view is not implemented
 * @param settings - Flight case settings
 * @returns The viewing side
 */
function getViewingSide(settings: FlightCaseSettings): Sides {
  // Check all cuts - if reference side is LEFT, RIGHT, FRONT or BACK, return it
  if (settings.cuts && settings.cuts.length > 0) {
    for (const cut of settings.cuts) {
      if (!cut) continue;
      const reference = cut.reference;
      if (
        reference === Sides.LEFT ||
        reference === Sides.RIGHT ||
        reference === Sides.FRONT ||
        reference === Sides.BACK
      ) {
        return reference;
      }
    }
  }

  return Sides.TOP;
}

/**
 * Extract volume size from different formats
 */
function extractVolumeSize(
  size: Point3DOrTuple | undefined,
): [number, number, number] {
  if (!size) return [0, 0, 0];
  if (Array.isArray(size)) {
    return [size[0] ?? 0, size[1] ?? 0, size[2] ?? 0];
  }
  return [size.x ?? 0, size.y ?? 0, size.z ?? 0];
}

/**
 * Process a panel or tray panel part
 * @param part - The part definition
 * @param viewingSide - The side we're viewing from (panels on this side are hidden)
 * @param panels - Array to collect panel data
 */

/**
 * Extract RGB color from PanelColor object
 */
function extractColorFromPanelColor(
  color: unknown,
): { r: number; g: number; b: number } | undefined {
  if (!color) {
    return undefined;
  }

  // Check if it's a PanelColor object with color property
  if (typeof color === "object" && color !== null) {
    const panelColor = color as { color?: string };
    if (panelColor.color && typeof panelColor.color === "string") {
      // Check if it's a hex color
      if (panelColor.color.startsWith("#")) {
        try {
          return hexToRgb(panelColor.color);
        } catch {
          return undefined;
        }
      }
    }
  }

  return undefined;
}

function processPanel(
  part: AbstractPartDefinition<string>,
  viewingSide: Sides,
  panels: Array<{
    position: Point3D;
    size: { x: number; y: number };
    side: number;
    strength: number;
    orientation?: number;
    color?: { r: number; g: number; b: number };
  }>,
): void {
  const props = part as unknown as {
    position?: Point3D;
    size?: { x: number; y: number };
    side?: number;
    strength?: number;
    orientation?: number;
    color?: unknown;
  };

  if (!props.position || !props.size || props.side === undefined) {
    return;
  }

  // Skip panels that are on the viewing side (they would block the view)
  if (props.side === viewingSide) {
    return;
  }

  // Extract color from panel material
  const color = extractColorFromPanelColor(props.color);

  panels.push({
    position: {
      x: props.position.x,
      y: props.position.y,
      z: props.position.z,
    },
    size: { x: props.size.x, y: props.size.y },
    side: props.side,
    strength: props.strength ?? 5,
    orientation: props.orientation,
    color,
  });
}

/**
 * Process a volume part
 */
function processVolume(
  part: AbstractPartDefinition<string>,
  volumes: Array<{
    position: Point3D;
    size: [number, number, number];
    side?: number;
  }>,
): void {
  const props = part as unknown as {
    position?: Point3D;
    size?: Point3DOrTuple;
    side?: number;
  };

  if (!props.position || !props.size) {
    return;
  }

  const size = extractVolumeSize(props.size);
  volumes.push({
    position: {
      x: props.position.x,
      y: props.position.y,
      z: props.position.z,
    },
    size,
    side: props.side,
  });
}

/**
 * Process a pad part
 * @param part - The part definition
 * @param viewingSide - The side we're viewing from (pads on this side are hidden)
 * @param pads - Array to collect pad data
 */
function processPad(
  part: AbstractPartDefinition<string>,
  viewingSide: Sides,
  pads: Array<{
    position: Point3D;
    size: [number, number];
    strength: number;
    side?: number;
  }>,
): void {
  const props = part as unknown as {
    position?: Point3D;
    size?: [number, number] | { x: number; y: number; z?: number };
    strength?: number;
    side?: number;
  };

  if (!props.position) {
    return;
  }

  // Skip pads that are on the viewing side (they would block the view)
  if (props.side === viewingSide) {
    return;
  }

  const { width, depth } = extractPadSize(props.size);
  const strength = props.strength ?? 0;

  pads.push({
    position: {
      x: props.position.x,
      y: props.position.y,
      z: props.position.z,
    },
    size: [width, depth],
    strength,
    side: props.side,
  });
}

/**
 * Collect interior layout data for 2D rendering from partDefinitions
 * Uses actual TrayPanel parts for accurate representation
 */
function collectInteriorData(
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
  blueprint: FlightCaseBlueprint,
  settings: FlightCaseSettings,
): InteriorLayoutData {
  const panels: Array<{
    position: Point3D;
    size: { x: number; y: number };
    side: number;
    strength: number;
    orientation?: number;
    color?: { r: number; g: number; b: number };
  }> = [];

  const volumes: Array<{
    position: Point3D;
    size: [number, number, number];
    side?: number;
  }> = [];

  const pads: Array<{
    position: Point3D;
    size: [number, number];
    strength: number;
    side?: number;
  }> = [];

  const viewingSide = getViewingSide(settings);

  for (const [, part] of partDefinitions) {
    if (part.type === LTPartType.Panel || part.type === LTPartType.TrayPanel) {
      processPanel(part, viewingSide, panels);
    } else if (part.type === LTPartType.Volume) {
      processVolume(part, volumes);
    } else if (part.type === LTPartType.Pad) {
      processPad(part, viewingSide, pads);
    }
  }

  const innerDimensions = blueprint.innerDimensions
    ? {
        x: blueprint.innerDimensions.x,
        y: blueprint.innerDimensions.y,
        z: blueprint.innerDimensions.z,
      }
    : undefined;

  return {
    panels,
    volumes: volumes.length > 0 ? volumes : undefined,
    pads: pads.length > 0 ? pads : undefined,
    innerDimensions,
    viewDirection: viewingSide,
  };
}

/**
 * Convert PdfInputData to pdfme input format for Page 2 (Interior + Dividers + Paddings)
 * Note: Does NOT spread emptyDruckplanInputs to avoid overwriting page1 values when merged
 */
async function toPage2Inputs(
  data: PdfInputData,
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
  blueprint: FlightCaseBlueprint,
  settings: FlightCaseSettings,
): Promise<Record<string, string | { orderId: string }>> {
  // Collect interior layout data from actual TrayPanel parts
  const interiorData = collectInteriorData(
    partDefinitions,
    blueprint,
    settings,
  );

  return {
    // Interior
    interiorHeader: "Übersicht Innenraum",
    interiorLayout: JSON.stringify(interiorData),

    // Dividers
    dividersHeader: "Trennbretter",
    dividersTable: await formatDividersTable(data.panelDividers),

    // Paddings
    paddingsHeader: "Schaumstoff",
    paddingsTable: await formatPaddingsTable(data.paddings),

    // Footer data is already set in page1Inputs, no need to set it again
  };
}

// =============================================================================
// TEMPLATE FILTERING HELPERS
// =============================================================================

/**
 * Check if a JSON table string is empty or has no rows
 */
function isTableEmpty(tableJson: string | undefined): boolean {
  if (!tableJson || tableJson === "[]" || tableJson.trim() === "") {
    return true;
  }
  try {
    const tableData = JSON.parse(tableJson);
    return tableData.rows && tableData.rows.length === 0;
  } catch {
    return true;
  }
}

/**
 * Check if a JSON table string has rows
 */
function hasTableRows(tableJson: string | undefined): boolean {
  if (!tableJson || tableJson === "[]" || tableJson.trim() === "") {
    return false;
  }
  try {
    const tableData = JSON.parse(tableJson);
    return tableData.rows && tableData.rows.length > 0;
  } catch {
    return false;
  }
}

/**
 * Check if a JSON array string has items
 */
function hasArrayItems(arrayJson: string | undefined): boolean {
  if (!arrayJson || arrayJson === "[]" || arrayJson.trim().length === 0) {
    return false;
  }
  try {
    const data = JSON.parse(arrayJson);
    return Array.isArray(data) && data.length > 0;
  } catch {
    return false;
  }
}

/**
 * Filter out schemas that have no data to avoid pdfme parsing errors
 */
function createFilteredTemplate(
  template: Template,
  inputs: Record<string, string>[],
): Template {
  const imageFields = ["previewImage", "rackRailsImage"];
  const inputData = inputs[0] ?? {};

  const filteredSchemas = template.schemas.map((pageSchemas: Schema[]) =>
    pageSchemas.filter((schema: Schema) =>
      shouldIncludeSchema(schema, inputData, imageFields),
    ),
  );

  return { ...template, schemas: filteredSchemas };
}

/**
 * Determine if a schema should be included based on its data
 */
function shouldIncludeSchema(
  schema: Schema,
  inputData: Record<string, string>,
  imageFields: string[],
): boolean {
  const name = schema.name;

  // Filter empty image schemas
  if (
    (schema.type === "image" || schema.type === "casePreviewImage") &&
    name &&
    imageFields.includes(name)
  ) {
    return Boolean(inputData[name] && inputData[name].length > 0);
  }

  // Filter underlays section if no underlays
  if (name === "underlaysTable") {
    return !isTableEmpty(inputData.underlaysTable);
  }

  // Filter dividers section if no dividers
  if (name === "dividersHeader" || name === "dividersTable") {
    return !isTableEmpty(inputData.dividersTable);
  }

  // Filter paddings section if no paddings
  if (name === "paddingsHeader" || name === "paddingsTable") {
    return !isTableEmpty(inputData.paddingsTable);
  }

  // Filter interior layout if no dividers and no paddings
  if (name === "interiorHeader" || name === "interiorLayout") {
    const hasDividers = hasTableRows(inputData.dividersTable);
    const hasPaddings = hasTableRows(inputData.paddingsTable);
    return hasDividers || hasPaddings;
  }

  // Filter rack rails section if no rack rails
  if (
    name === "rackRailsTable" ||
    name === "rackRailsImage" ||
    name === "rackRailPreview"
  ) {
    const hasRackRailsTable = hasTableRows(inputData.rackRailsTable);
    const hasRackRailPreview =
      inputData.rackRailPreview &&
      inputData.rackRailPreview !== "[]" &&
      inputData.rackRailPreview.trim().length > 0;
    return hasRackRailsTable || Boolean(hasRackRailPreview);
  }

  // Filter left column profile preview if no profile data
  if (name === "profilePreviewsLeftHeader" || name === "profilePreviewsLeft") {
    return hasArrayItems(inputData.profilePreviewsLeft);
  }

  return true;
}

// =============================================================================
// PDF GENERATION
// =============================================================================

/**
 * Generate a Druckplan PDF from product data
 */
export async function generateDruckplan(
  settings: FlightCaseSettings,
  blueprint: FlightCaseBlueprint,
  partDefinitions: Map<string, AbstractPartDefinition<string>>,
  positionInput: PdfPositionInput,
  previewImage?: string,
  boundingBox?: { x: number; y: number; z: number } | null,
): Promise<Uint8Array> {
  // Dynamic import to avoid bundling issues with pdfme
  const { generate } = await import("@pdfme/generator");
  const { text, image, table, svg } = await import("@pdfme/schemas");

  const data = collectPdfData(
    settings,
    blueprint,
    partDefinitions,
    positionInput,
  );

  if (previewImage) {
    data.previewImage = previewImage;
  }

  // Get panel color and strength for subheader
  const panelsColor = settings.exteriorOptions?.color?.description ?? "";
  const panelsStrength = String(settings.exteriorOptions?.strength ?? "");

  // Check if rack rails are present
  const hasRackRails = data.rackRails.length > 0;

  // Check if profile previews are available (profiles with points for 2D rendering)
  const hasProfilePreviews = data.profiles.some(
    (p) => p.points && Array.isArray(p.points) && p.points.length >= 2,
  );

  // Calculate rack rails table rows (grouped by symbol and length)
  const rackRailsGrouped = new Map<string, number>();
  for (const r of data.rackRails) {
    const key = `${r.symbol || ""}-${r.length}`;
    rackRailsGrouped.set(key, (rackRailsGrouped.get(key) || 0) + 1);
  }
  const rackRailsTableRows = rackRailsGrouped.size;

  // Calculate underlays table rows
  const underlaysTableRows = data.panelUnderlays.length;

  // Calculate dynamic heights for positioning
  const dynamicHeights: DynamicHeights = {
    panelsTableRows: data.panels.length,
    profilePreviewsVisible: hasProfilePreviews,
    angleSectionsTableRows: data.angleSections.reduce(
      (total, group) => total + group.entries.length,
      0,
    ),
    angleSectionsGroups: data.angleSections,
    rackRailsTableRows,
    underlaysTableRows,
  };

  // Create single merged input object for all pages
  // pdfme uses ONE input object per document, schemas define the pages
  const page1Inputs = await toPage1Inputs(
    data,
    panelsColor,
    panelsStrength,
    blueprint,
    positionInput,
    boundingBox,
  );
  const page2Inputs = await toPage2Inputs(
    data,
    partDefinitions,
    blueprint,
    settings,
  );
  const mergedInputs = { ...page1Inputs, ...page2Inputs } as Record<
    string,
    string
  >;

  const template = getDynamicTemplate(hasRackRails, dynamicHeights);

  // Create template without empty image schemas
  const filteredTemplate = createFilteredTemplate(template, [mergedInputs]);

  // Import custom schemas
  const { casePreviewImageSchema } = await import("./casePreviewImageSchema");
  const { interiorLayout } = await import("./interiorSchema");
  const { profileTableSchema } = await import("./profileTableSchema");
  const { profilePreviewSchema } = await import("./profilePreviewSchema");
  const { profileCrossSectionSchema } = await import(
    "./profileCrossSectionSchema"
  );
  const { rackRailPreviewSchema } = await import("./rackRailPreviewSchema");
  const { tableSchema } = await import("./tableSchema");
  const { panelsHeaderSchema } = await import("./panelsHeaderSchema");
  const { framedHeaderSchema } = await import("./framedHeaderSchema");
  const { boldLabelSchema } = await import("./boldLabelSchema");
  const { footerSchema } = await import("./footerSchema");
  const { productHeaderPdfSchema } = await import("./productHeader");

  const pdf = await generate({
    template: filteredTemplate,
    inputs: [mergedInputs],
    plugins: {
      text,
      image,
      casePreviewImage: casePreviewImageSchema,
      table, // Keep standard table for header tables
      svg, // Native SVG rendering for productHeader
      customTable: tableSchema, // Our custom table for data tables
      interiorLayout,
      profileTable: profileTableSchema,
      profilePreview: profilePreviewSchema,
      profileCrossSection: profileCrossSectionSchema, // Cross-section above 3D preview
      rackRailPreview: rackRailPreviewSchema,
      panelsHeader: panelsHeaderSchema,
      framedHeader: framedHeaderSchema,
      boldLabel: boldLabelSchema,
      footer: footerSchema,
      productHeaderPdf: productHeaderPdfSchema,
      // biome-ignore lint/suspicious/noExplicitAny: pdfme Plugin type complexity mismatch
    } as any,
    options: {
      author: "LT Cases",
      creator: "Feinarbyte GmbH",
      producer: "LT Cases Provision",
      title: `${settings.type} - ${positionInput.model || "Plan"}`,
    },
  });

  return pdf;
}

// =============================================================================
// DOWNLOAD / PREVIEW HELPERS
// =============================================================================

/**
 * Trigger a browser download for the PDF
 */
export function downloadPdf(pdfBytes: Uint8Array, filename: string): void {
  const blob = new Blob([new Uint8Array(pdfBytes)], {
    type: "application/pdf",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Open PDF in a new browser tab for preview
 */
export function previewPdf(pdfBytes: Uint8Array): void {
  const blob = new Blob([new Uint8Array(pdfBytes)], {
    type: "application/pdf",
  });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}
